import React from "react";
import { formatKst } from "../../utils/time";

const STALE_THRESHOLD_MS = 60 * 1000;

const SERVICE_DISPLAY_NAMES = {
    deltafx: "DeltaFX",
    risk_manager: "Risk Manager",
};

const REASON_LABELS = {
    scheduled: "예약",
    suspended_by_schedule: "예약 중단",
    manual_override: "수동",
    UNKNOWN: "알 수 없음",
};

const ORDERED_SERVICES = [
    "deltafx",
    "risk_manager",
];

const getCardStatus = (service, now) => {
    if (!service) {
        return { label: "DISCONNECTED", badgeClass: "bg-surface-hover text-content-secondary", borderLeftClass: "border-l-surface-border", enabled: false };
    }
    const heartbeatAt = service.heartbeat_at ? new Date(service.heartbeat_at) : null;
    if (heartbeatAt && now - heartbeatAt > STALE_THRESHOLD_MS) {
        return { label: "STALE", badgeClass: "bg-warning text-content-inverse", borderLeftClass: "border-l-warning", enabled: true };
    }
    if (service.enabled) {
        return { label: "ACTIVE", badgeClass: "bg-gain text-content-inverse", borderLeftClass: "border-l-gain", enabled: true };
    }
    return { label: "INACTIVE", badgeClass: "bg-loss text-content-inverse", borderLeftClass: "border-l-loss", enabled: false };
};

const formatKST = (isoString) => formatKst(isoString, "MM-DD HH:mm:ss");

class StrategyServiceStatusBoard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            now: new Date(),
        };
        this.tickInterval = null;
    }

    componentDidMount() {
        this.tickInterval = setInterval(() => this.setState({ now: new Date() }), 5000);
    }

    componentWillUnmount() {
        if (this.tickInterval) clearInterval(this.tickInterval);
    }

    render() {
        const { services } = this.props;
        const { now } = this.state;

        const serviceMap = {};
        if (Array.isArray(services)) {
            services.forEach((s) => {
                serviceMap[s.name] = s;
            });
        }

        return (
            <div className="rounded-xl border border-surface-border/60 p-2.5 sm:p-3">
                <div className="flex items-center gap-2 pl-0.5 text-xs font-medium uppercase tracking-wide text-content-muted">
                    <span className="h-1 w-1 rounded-full bg-content-muted" aria-hidden="true" />
                    전략 서비스 상태
                </div>

                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {ORDERED_SERVICES.map((name) => {
                        const service = serviceMap[name] || null;
                        const { label, badgeClass, borderLeftClass } = getCardStatus(service, now);
                        const displayName = SERVICE_DISPLAY_NAMES[name] || name;
                        const rawReason = service?.reason || null;
                        const reasonLabel = rawReason
                            ? (REASON_LABELS[rawReason] ?? REASON_LABELS["UNKNOWN"])
                            : null;
                        const updatedAt = service?.updated_at || null;

                        return (
                            <div
                                key={name}
                                className={`rounded-xl border border-surface-border border-l-4 ${borderLeftClass} bg-surface-elevated p-3`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-content">
                                        {displayName}
                                    </span>
                                    <span
                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${badgeClass}`}
                                        aria-label={`${displayName} 상태: ${label}`}
                                    >
                                        {label}
                                    </span>
                                </div>

                                <div className="mt-2 space-y-1">
                                    {reasonLabel && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-content-muted">사유</span>
                                            <span className="rounded-md bg-surface-hover px-1.5 py-0.5 text-xs text-content-secondary">
                                                {reasonLabel}
                                            </span>
                                        </div>
                                    )}

                                    <div className="text-xs text-content-muted">
                                        {updatedAt
                                            ? `갱신: ${formatKST(updatedAt)}`
                                            : service
                                            ? "갱신 정보 없음"
                                            : "연결 안 됨"}
                                    </div>

                                    {label === "STALE" && (
                                        <div className="mt-1 text-xs text-warning">
                                            heartbeat 60초 초과 — 활성 간주 (fail-open)
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`mt-2 h-1.5 w-full rounded-full ${
                                        label === "ACTIVE"
                                            ? "bg-gain"
                                            : label === "STALE"
                                            ? "bg-warning"
                                            : label === "DISCONNECTED"
                                            ? "bg-surface-hover"
                                            : "bg-loss"
                                    }`}
                                    aria-hidden="true"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default StrategyServiceStatusBoard;
